// saturday-event.js
// Loads Google Calendar ICS feed (via CORS proxy), finds the next Saturday event,
// and exposes it on window.TG_SATURDAY_EVENT

console.log("Saturday Event Loader: starting…");

// ---- CONFIG ----
const DIRECT_ICS =
  "https://calendar.google.com/calendar/ical/b2529d0528779531b649f027c52cd63f5df21fb1196c82e952021849e8edc276%40group.calendar.google.com/public/basic.ics";

const PROXIES = [
  // api.codetabs.com removed 2026-09-03. It returns HTTP 522 with NO
  // Access-Control-Allow-Origin, so it can never succeed from a browser — and it
  // was FIRST in this list, so every page load wasted a request and logged a CORS
  // error before falling through to the worker below. Do not re-add it.
  // Measured the same day: the worker is the ONLY working entry here (200, full
  // feed, ACAO *). api.allorigins.win also returns 522 and corsproxy.io returns
  // 403, so the two below the worker are dead too — left in place only because
  // the loop never reaches them while the worker answers. Treat this chain as
  // having no real fallback.
  "https://tg-ics-proxy.royal-dream-93db.workers.dev",
  // Supabase ical-proxy, added 2026-09-03 as a REAL fallback. The three public
  // proxies that used to sit below the worker are all dead (codetabs 522,
  // allorigins 522, corsproxy.io 403), so until now the worker had no working
  // backup and the whole site's calendar hung on it.
  // This one is self-hosted, already in production for the TV leaderboard, needs
  // no auth (verify_jwt is off), and returns 200 with Access-Control-Allow-Origin
  // '*' plus the full feed. Verified it serves the SAME calendar before wiring it
  // in: identical 120 UIDs and identical sorted SUMMARY list against a direct
  // fetch. The VEVENT order differs, which is fine — every consumer here parses
  // the whole feed rather than relying on order.
  // The URL takes no query parameter: the calendar is fixed inside the function,
  // so DIRECT_ICS is deliberately not appended.
  "https://emqkrszhbqydkujminhi.supabase.co/functions/v1/ical-proxy",
  "https://api.allorigins.win/raw?url=" + encodeURIComponent(DIRECT_ICS),
  "https://corsproxy.io/?" + encodeURIComponent(DIRECT_ICS),
];

// ---- HELPERS ----

// Turn Date -> "YYYYMMDD"
function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

// Format time like "7:30 PM"
function formatTime(date) {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const mins = minutes.toString().padStart(2, "0");
  return `${hours}:${mins} ${ampm}`;
}

/**
 * Parse a DTSTART line from ICS into a local Date.
 * Handles:
 *  - DTSTART:20251115
 *  - DTSTART:20251116T073000Z
 *  - DTSTART;TZID=America/Los_Angeles:20251115T190000
 */
function parseICSDate(block) {
  // Grab the whole DTSTART line
  const lineMatch = block.match(/DTSTART[^\r\n]*/);
  if (!lineMatch) return null;
  const line = lineMatch[0];

  // Extract TZID (if present), date part, and time part
  const m = line.match(/DTSTART(?:;TZID=([^:]*))?:(\d{8})(T\d{6}Z?)?/);
  if (!m) return null;

  const tzid = m[1] || null;
  const datePart = m[2];          // "20251116"
  const timePart = m[3] || null;  // "T073000Z" or "T190000" or null

  const year = parseInt(datePart.slice(0, 4), 10);
  const month = parseInt(datePart.slice(4, 6), 10) - 1;
  const day = parseInt(datePart.slice(6, 8), 10);

  // All-day event -> local midnight
  if (!timePart) {
    return new Date(year, month, day, 0, 0, 0);
  }

  const hh = parseInt(timePart.slice(1, 3), 10);
  const mm = parseInt(timePart.slice(3, 5), 10);
  const ss = parseInt(timePart.slice(5, 7), 10);
  const isUTC = timePart.endsWith("Z");

  if (isUTC || !tzid) {
    // Treat as UTC, then JS converts to STORE VIEWER'S local time
    return new Date(Date.UTC(year, month, day, hh, mm, ss));
  }

  // If there's a TZID (e.g. America/Los_Angeles), treat as local time for this browser.
  // This is good enough for our use-case (store is Pacific).
  return new Date(year, month, day, hh, mm, ss);
}

/**
 * Parse ICS text into event objects:
 * { title, startLocal: Date, localDateKey }
 */
function parseEvents(text) {
  if (!text.includes("BEGIN:VEVENT")) return [];

  const blocks = text.split("BEGIN:VEVENT").slice(1);
  const events = [];

  for (const block of blocks) {
    const startLocal = parseICSDate(block);
    if (!startLocal) continue;

    const localDateKey = toDateKey(startLocal);

    const titleMatch = block.match(/SUMMARY:(.*)/);
    const title = titleMatch ? titleMatch[1].trim() : "Unnamed Event";

    events.push({
      title,
      startLocal,
      localDateKey
    });
  }

  return events;
}

// ---- HELPERS ----
async function fetchICS() {
  for (let i = 0; i < PROXIES.length; i++) {
    try {
      const res = await fetch(PROXIES[i]);
      if (!res.ok) throw new Error("HTTP " + res.status);
      const text = await res.text();
      if (!text.includes("BEGIN:VCALENDAR")) throw new Error("Not ICS");
      return text;
    } catch (err) {
      console.warn("ICS proxy[" + i + "] failed:", err.message);
    }
  }
  throw new Error("All proxies failed");
}

// ---- MAIN LOADER ----
async function loadSaturdayEvent() {
  try {
    console.log("Fetching ICS…");
    const text = await fetchICS();

    const events = parseEvents(text);
    console.log("Parsed events:", events.length);

    // Figure out "this Saturday" in viewer's local time
    const today = new Date();
    const saturday = new Date(today);
    saturday.setHours(0, 0, 0, 0);
    saturday.setDate(today.getDate() + ((6 - today.getDay() + 7) % 7));
    const satKey = toDateKey(saturday);

    console.log("Looking for Saturday:", satKey);

    let found = null;

    for (const ev of events) {
      console.log("Checking event:", {
        rawStart: ev.startLocal.toISOString(),
        localDateKey: ev.localDateKey,
        satKey
      });

      if (ev.localDateKey === satKey) {
        // Choose the earliest event if multiple
        if (!found || ev.startLocal < found.startLocal) {
          found = ev;
        }
      }
    }

    if (found) {
      const timeStr = formatTime(found.startLocal);
      window.TG_SATURDAY_EVENT = {
        title: found.title,
        display: `Saturday @ ${timeStr}`,
        url: "/pages/events"
      };
      console.log("FOUND Saturday event:", window.TG_SATURDAY_EVENT);
    } else {
      window.TG_SATURDAY_EVENT = null;
      console.log("No Saturday event found.");
    }
  } catch (err) {
    console.error("ICS ERROR:", err);
    window.TG_SATURDAY_EVENT = null;
  }
}

// Run loader
loadSaturdayEvent();
